import React, { FC, useCallback } from 'react';

import { Node } from 'src/common/interfaces/node.interface';
import { Step } from 'src/common/interfaces/step.interface';
import { apiDetachNodeFromSynced, apiReplaceNodeInJourney } from 'src/utils/journeyApi';
import { useEditorContext } from 'src/store/editor';
import { EditorConfirmPanel } from '../confirm-panel';
import { JourneyMenuPropsInterface } from 'src/common/JourneyMenu';
import { useNotificationMessage } from 'src/common/notification/message.hook';

interface SyncedStepEditPanelProps
  extends Pick<
    JourneyMenuPropsInterface,
    'renderOrigin' | 'openRef' | 'closeRef' | 'onOpen' | 'onClose' | 'placement' | 'offset'
  > {
  node: Node;
  step: Step;
  onBack?: () => void;
  children?: any;
  open?: boolean;
}

export const SyncedStepEditPanel: FC<SyncedStepEditPanelProps> = ({
  node,
  step,
  renderOrigin,
  openRef,
  closeRef,
  offset,
  placement,
  onOpen,
  onClose,
  onBack,
}) => {
  const { journey, setJourney } = useEditorContext();
  const { setSuccessNotification } = useNotificationMessage();

  const onConfirm = useCallback(() => {
    apiDetachNodeFromSynced(node.id, journey.workspace.id).then((nodeResponse) => {
      apiReplaceNodeInJourney(journey.uuid, step.id, node.uuid, nodeResponse.uuid).then((journey) => {
        setJourney(journey);        
        onClose && onClose();
        setSuccessNotification('Synced step successfully detached.');
      });
    });
  }, [node, journey, step, onClose, setJourney, setSuccessNotification]);

  const onDeny = useCallback(() => {
    onClose && onClose();
  }, [onClose]);

  return (
    <EditorConfirmPanel
      renderOrigin={renderOrigin}
      offset={offset}
      placement={placement}
      openRef={openRef}
      closeRef={closeRef}
      copy={
        <div className='flex flex-col space-y-2'>
          <div className='text-bedrock-p-strong text-bedrock-black'>{node.synced_name || node.name}</div>
          <div className='text-bedrock-p text-bedrock-black cursor-default'>
            This is a Synced Step. Detach if you wish to make independent changes to this Step.
          </div>
        </div>
      }
      onBack={onBack}
      confirmCopy='Yes, detach'
      denyCopy='No, keep'
      onConfirm={onConfirm}
      onDeny={onDeny}
      onOpen={onOpen}
      onClose={onClose}
    />
  );
};
